<template>
  <div class="dialog-container" v-loading="isLoading">
    <el-alert class="mb-3" :title="errorMessage" type="error" show-icon v-if="errorMessage"></el-alert>
    <el-row :gutter="18">
      <el-col :span="24">
        <el-form ref="saveClient" :model="form" :rules="rules" label-width="175px">
          <el-form-item label="First Name" prop="firstName">
            <el-input v-model="form.firstName" placeholder="First Name"></el-input>
          </el-form-item>
          <el-form-item label="Last Name" prop="lastName">
            <el-input v-model="form.lastName" placeholder="Last Name"></el-input>
          </el-form-item>
          <el-form-item label="SSN" prop="ssn">
            <masked-el-input v-model="form.ssn" :mask="'###-##-####'"
              placeholder="Social Security Number"></masked-el-input>
          </el-form-item>
          <el-form-item label="Driver's License" prop="stateId">
            <el-input v-model="form.stateId" placeholder="Driver's License"></el-input>
          </el-form-item>
          <el-form-item label="Email Address" prop="email">
            <el-input v-model="form.email" placeholder="Email Address"></el-input>
          </el-form-item>
          <el-form-item label="Phone" prop="phone">
            <el-input v-model="form.phone" v-mask="'(###) ###-####'" placeholder="Phone"></el-input>
          </el-form-item>
          <el-form-item label="Office" prop="officeId" v-if="isAdmin">
            <el-select v-model="form.officeId" value-key="id" :disabled="allUserOffices.length === 1">
              <el-option v-for="item in formattedUserOffices" :key="item.id" :label="item.name" :value="item.id">
              </el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>
    <el-row :gutter="18" class="dialog-footer">
      <el-col :span="24" class="align-right">
        <el-button @click="closeDialog">Cancel</el-button>
        <el-button type="primary" @click="saveClient">Save Client</el-button>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import get from 'lodash/get';
import map from 'lodash/map';
import { Phone, Required, SSN } from '@/util/formValidators';
import Client from '@/model/Client';
import MaskedElInput from '@/components/form-item/MaskedElInput.vue';
import OfficeToRefType from '@/util/OfficeToRefType';

export default {
  name: 'EditClientDialog',
  props: {
    client: {
      type: Client,
      default() {
        return new Client();
      },
    },
    isSpouse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      form: new Client(),
      rules: {
        firstName: [Required],
        lastName: [Required],
        ssn: [Required, SSN],
        stateId: [Required],
        officeId: [Required],
        phone: [Required, Phone],
      },
    };
  },
  methods: {
    ...mapActions('Client', [
      'updateClient',
      'updateSpouse',
      'save',
    ]),
    async saveClient() {
      this.errorMessage = null;
      this.$refs.saveClient.validate(async (valid) => {
        if (valid) {
          this.isLoading = true;
          if (this.isSpouse) {
            this.updateSpouse(this.form);
          } else {
            this.updateClient(this.form);
          }
          try {
            const updatedClient = await this.save();

            this.$notify.success({
              title: 'Success',
              message: 'Client saved.',
              position: 'bottom-right',
            });

            this.$emit('clientUpdated', updatedClient);
            this.clearForm();
            this.closeDialog();
          } catch (e) {
            this.errorMessage = get(e, 'response.data', e.message || 'An error occurred.');
          } finally {
            this.isLoading = false;
          }

          return true;
        }
        return false;
      });
    },
    closeDialog() {
      this.$emit('close');
    },
    initForm(client) {
      this.clearForm();
      if (client) {
        const formData = new Client(this.client);
        // using object.assign here because it triggers the setter
        // using spread operator did not update date-input
        this.$nextTick(() => {
          Object.assign(this.form, formData);
          if (this.form.officeId == null) {
            this.form.officeId = this.activeOffice;
          }
        });
      }
    },
    clearForm() {
      Object.keys(this.form).forEach((key) => {
        this.form[key] = null;
      });
      this.$refs.saveClient.resetFields();
    },
  },
  watch: {
    client(val) {
      this.initForm(val);
    },
    activeOffice(activeOfficeId) {
      if (this.form.id === undefined) {
        this.form.officeId = activeOfficeId;
      }
    },
  },
  computed: {
    ...mapGetters('User', [
      'isAdmin',
      'activeOffice',
      'allUserOffices',
    ]),
    formattedUserOffices() {
      return map(this.allUserOffices, office => OfficeToRefType(office));
    },
  },
  mounted() {
    if (this.client) {
      this.initForm(this.client);
    } else {
      this.form.officeId = this.activeOffice;
    }
  },
  components: {
    MaskedElInput,
  },
};

</script>
